"use client"
import NavBar from "@/components/navigation/NavBar"
import Footer from "@/components/navigation/Footer"
import { motion } from "framer-motion"
import { MapPin, Phone, Mail, Clock } from "lucide-react"
import ContactForm from "@/components/form/ContactForm"

export default function KontaktPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariant = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <div className="font-serif">
      <NavBar className="bg-opacity-100 font-sans border-none shadow-none" />

      <div className="pt-24 md:pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Kontaktieren Sie uns</h1>
            <p className="text-xl text-text-100 max-w-3xl mx-auto">
              Haben Sie Fragen oder Anliegen? Unser Team steht Ihnen gerne zur Verfügung.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="bg-prime-50 p-8 rounded-lg"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Unsere Kontaktdaten</h2>

              <motion.div className="space-y-6" variants={staggerContainer}>
                <motion.div className="flex items-start" variants={itemVariant}>
                  <MapPin className="w-6 h-6 mr-4 text-prime-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Adresse</h3>
                    <p className="text-text-100">
                      Uferstr 40
                      <br />
                      52249 Eschweiler
                    </p>
                  </div>
                </motion.div>

                <motion.div className="flex items-start" variants={itemVariant}>
                  <Phone className="w-6 h-6 mr-4 text-prime-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Telefon</h3>
                    <p className="text-text-100"><a href="tel:02403 883170">+49 (0) 2403 883170</a></p>
                  </div>
                </motion.div>

                <motion.div className="flex items-start" variants={itemVariant}>
                  <Mail className="w-6 h-6 mr-4 text-prime-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">E-Mail</h3>
                    <p className="text-text-100">info@jakobs-immobilien.de</p>
                  </div>
                </motion.div>

                <motion.div className="flex items-start" variants={itemVariant}>
                  <Clock className="w-6 h-6 mr-4 text-prime-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Öffnungszeiten</h3>
                    <p className="text-text-100">
                      Mo-Sa: 9:00 - 20:00 Uhr
                      <br />
                      Bürozeiten: Nach Vereinbarung
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div
                className="mt-16 relative h-64 rounded-lg overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <img
                  src="/Fotos/ueber-buero.jpg"
                  alt="Helga Jakobs Immobilien Büro"
                  fill
                  className="rounded-lg object-cover"
                />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Schreiben Sie uns</h2>
                <ContactForm fields={["name", "email", "phone", "message", "agbAccepted"]} button="Nachricht senden" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        className="py-16 bg-gray-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Wie wir Ihnen helfen können</h2>
            <p className="text-lg text-text-100 max-w-3xl mx-auto">
            Sie haben Fragen – wir nehmen uns Zeit. Ganz persönlich.
            </p>
          </div>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="bg-white p-6 rounded-lg shadow-sm" variants={itemVariant}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <img src="/Icons/header-bewertung.svg" alt="" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Immobilienbewertung</h3>
              <p className="text-text-100">
              Sie möchten wissen, was Ihre Immobilie wert ist?
              Wir liefern Ihnen eine fundierte Einschätzung – transparent, ehrlich und unverbindlich.
              </p>
            </motion.div>

            <motion.div className="bg-white p-6 rounded-lg shadow-sm" variants={itemVariant}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <img src="/Icons/header-verkauf.svg" alt="" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Immobilienverkauf</h3>
              <p className="text-text-100">
              Ob geerbtes Elternhaus oder lange vertrautes Zuhause:
              Wir begleiten Sie beim Verkauf mit Erfahrung, Respekt und einem klaren Plan.
              </p>
            </motion.div>

            <motion.div className="bg-white p-6 rounded-lg shadow-sm" variants={itemVariant}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <img src="/Icons/header-angebot.svg" alt="" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Immobilienkauf</h3>
              <p className="text-text-100">
              Sie suchen ein neues Zuhause oder eine passende Kapitalanlage?
              Wir hören zu, beraten ehrlich – und finden das Richtige für Sie.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <Footer />
    </div>
  )
}
